import { supabase } from "@/shared/lib/supabase";

// Get Supabase configuration using the same pattern as the working client
const getSupabaseConfig = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !key) {
    throw new Error("Missing Supabase environment variables");
  }

  return { url, key };
};

export interface EncryptionRequest {
  action: "encrypt" | "decrypt" | "decryptLast4";
  data: string;
  context: "user" | "person";
  contextId: number;
  field?: string;
}

export interface EncryptionResponse {
  success: boolean;
  data?: string;
  error?: string;
}

/**
 * Server-side encryption utility that calls the encryption Edge Function
 * This replaces the insecure client-side encryption
 */
class ServerEncryption {
  private async callEncryptionFunction(request: EncryptionRequest): Promise<EncryptionResponse> {
    try {
      // Get current session for auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        throw new Error("Authentication required for encryption operations");
      }

      // Call the encryption Edge Function
      const { url: supabaseUrl, key: supabaseAnonKey } = getSupabaseConfig();

      const response = await fetch(`${supabaseUrl}/functions/v1/security/encrypt`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Encryption function HTTP error:", response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Encryption operation failed");
      }

      return data;
    } catch (error) {
      console.error("Server encryption error:", error);
      throw error;
    }
  }

  /**
   * Encrypt sensitive data on the server
   */
  async encrypt(
    plaintext: string,
    context: "user" | "person",
    contextId: number,
    field?: string,
  ): Promise<string> {
    if (!plaintext || plaintext.trim() === "") {
      return plaintext; // Don't encrypt empty strings
    }

    const response = await this.callEncryptionFunction({
      action: "encrypt",
      data: plaintext,
      context,
      contextId,
      field,
    });

    if (!response.data) {
      throw new Error("Encryption failed - no data returned");
    }

    return response.data;
  }

  /**
   * Decrypt sensitive data on the server
   */
  async decrypt(
    encryptedData: string,
    context: "user" | "person",
    contextId: number,
    field?: string,
  ): Promise<string> {
    if (!encryptedData || encryptedData.trim() === "") {
      return encryptedData; // Return empty strings as-is
    }

    const response = await this.callEncryptionFunction({
      action: "decrypt",
      data: encryptedData,
      context,
      contextId,
      field,
    });

    if (!response.data) {
      throw new Error("Decryption failed - no data returned");
    }

    return response.data;
  }

  async decryptLast4(
    encryptedData: string,
    context: "user" | "person",
    contextId: number,
    field?: string,
  ): Promise<string> {
    if (!encryptedData || encryptedData.trim() === "") return "";

    const response = await this.callEncryptionFunction({
      action: "decryptLast4",
      data: encryptedData,
      context,
      contextId,
      field,
    });

    return response.data ?? "";
  }

  /**
   * Encrypt multiple fields for a user context
   */
  async encryptUserFields(
    userId: number,
    fields: Record<string, string>,
  ): Promise<Record<string, string>> {
    const encrypted: Record<string, string> = {};

    for (const [fieldName, value] of Object.entries(fields)) {
      if (value && value.trim() !== "") {
        encrypted[fieldName] = await this.encrypt(value, "user", userId, fieldName);
      } else {
        encrypted[fieldName] = value; // Keep empty values as-is
      }
    }

    return encrypted;
  }

  /**
   * Decrypt multiple fields for a user context
   */
  async decryptUserFields(
    userId: number,
    fields: Record<string, string>,
  ): Promise<Record<string, string>> {
    const decrypted: Record<string, string> = {};

    for (const [fieldName, value] of Object.entries(fields)) {
      if (value && value.trim() !== "") {
        try {
          decrypted[fieldName] = await this.decrypt(value, "user", userId, fieldName);
        } catch (error) {
          console.error(`Failed to decrypt user field ${fieldName}:`, error);
          decrypted[fieldName] = ""; // Return empty string on decrypt failure
        }
      } else {
        decrypted[fieldName] = value; // Keep empty values as-is
      }
    }

    return decrypted;
  }

  /**
   * Encrypt multiple fields for a person context
   */
  async encryptPersonFields(
    estateId: number,
    fields: Record<string, string>,
  ): Promise<Record<string, string>> {
    const encrypted: Record<string, string> = {};

    for (const [fieldName, value] of Object.entries(fields)) {
      if (value && value.trim() !== "") {
        encrypted[fieldName] = await this.encrypt(value, "person", estateId, fieldName);
      } else {
        encrypted[fieldName] = value; // Keep empty values as-is
      }
    }

    return encrypted;
  }

  /**
   * Decrypt multiple fields for a person context
   */
  async decryptPersonFields(
    estateId: number,
    fields: Record<string, string>,
  ): Promise<Record<string, string>> {
    const decrypted: Record<string, string> = {};

    for (const [fieldName, value] of Object.entries(fields)) {
      if (value && value.trim() !== "") {
        try {
          decrypted[fieldName] = await this.decrypt(value, "person", estateId, fieldName);
        } catch (error) {
          console.error(`Failed to decrypt person field ${fieldName}:`, error);
          decrypted[fieldName] = ""; // Return empty string on decrypt failure
        }
      } else {
        decrypted[fieldName] = value; // Keep empty values as-is
      }
    }

    return decrypted;
  }
}

// Export singleton instance
export const serverEncryption = new ServerEncryption();

// Helper functions for common use cases
export const encryptUserData = (userId: number, data: string, field?: string) =>
  serverEncryption.encrypt(data, "user", userId, field);

export const decryptUserData = (userId: number, encryptedData: string, field?: string) =>
  serverEncryption.decrypt(encryptedData, "user", userId, field);

export const encryptPersonData = (estateId: number, data: string, field?: string) =>
  serverEncryption.encrypt(data, "person", estateId, field);

export const decryptPersonData = (estateId: number, encryptedData: string, field?: string) =>
  serverEncryption.decrypt(encryptedData, "person", estateId, field);

export const decryptLast4UserData = (userId: number, encryptedData: string, field?: string) =>
  serverEncryption.decryptLast4(encryptedData, "user", userId, field);
